import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errDetail } from "@/lib/api";
import { prettyKey } from "@/lib/reportScope";

// R17 - the rows behind ONE number of a report. Every figure on the reconciled panels opens this dialog and shows exactly the members that were counted, a page at a time.
//
// Identity (R17 review F4): the rows on screen belong to ONE (request id, page) pair. The moment that pair changes - another cell, another page, the parent scope changing, closing, unmounting - the old rows
// are NOT rendered any more (not merely "ignored when a late answer arrives"): the dialog shows its loading state for the new pair, a late answer for an older pair is dropped, and an older pair's error
// never replaces the newer one. The export link is bound to the SAME request (it carries the scope the rows were fetched for), and the owning panel closes the dialog whenever its scope changes, so
// rows of one scope can never sit beside another scope's title or export.
const HIDDEN = new Set(["is_test_record", "contact_id"]);
const cell = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No") : Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v));

let REQUEST_SEQ = 0;

// The panel's rows request: `open({title, note, fetchPage})` stamps an id, the scope (`qs`) and an export link built from that SAME scope; a change of `qs` closes any open request.
export function useRowsRequest(qs, exportPath) {
  const [req, setReq] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => { setReq(null); }, [qs]);
  const open = useCallback((r) => setReq({ ...r, id: ++REQUEST_SEQ, qs, exportHref: exportPath ? `${backend}/api/reports/${exportPath}/export?${qs}` : null }), [qs, exportPath, backend]);
  const close = useCallback(() => setReq(null), []);
  return [req, open, close];
}

export function ReportRowsDialog({ request, onClose }) {
  const [pg, setPg] = useState({ id: null, page: 1 });
  const [state, setState] = useState({ key: null, loading: false, body: null, error: null });
  const gen = useRef(0);
  const page = request && pg.id === request.id ? pg.page : 1;          // a new request always starts on page 1 (never inherits the previous request's page)
  const key = request ? `${request.id}|${page}` : null;
  const setPage = (fn) => setPg({ id: request.id, page: typeof fn === "function" ? fn(page) : fn });

  useEffect(() => {
    const mine = ++gen.current;
    if (!request) { setState({ key: null, loading: false, body: null, error: null }); return undefined; }
    setState({ key, loading: true, body: null, error: null });
    Promise.resolve(request.fetchPage(page))
      .then((body) => { if (mine === gen.current) setState({ key, loading: false, body, error: null }); })
      .catch((e) => { if (mine === gen.current) setState({ key, loading: false, body: null, error: errDetail(e, "Could not load these rows") }); });
    return () => { gen.current += 1; };                                  // unmount / next pair: nothing from this pair may land later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const view = state.key === key ? state : { key, loading: true, body: null, error: null };       // only ever render the state that belongs to THIS (request, page)
  const body = view.body;
  const items = (body && body.items) || [];
  const columns = items.length ? Object.keys(items[0]).filter((k) => !HIDDEN.has(k)) : [];
  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="report-rows-dialog">
        <DialogHeader><DialogTitle className="font-heading">{request?.title}</DialogTitle></DialogHeader>
        {request?.note && <p className="text-xs text-muted-foreground">{request.note}</p>}
        {view.error ? (
          <p role="alert" className="text-sm text-[hsl(var(--danger))]" data-testid="report-rows-error">{view.error}</p>
        ) : !body ? (
          <p className="text-xs text-muted-foreground" data-testid="report-rows-loading">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="report-rows-empty">No records for this number with the current scope.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground" data-testid="report-rows-total">
              {body.truncated || body.total_is_lower_bound ? `At least ${body.total} records (the source was truncated${body.source_total ? `; ${body.source_total} match in the source` : ""})` : `${body.total} record${body.total === 1 ? "" : "s"}`} — showing {items.length} (page {page})
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{columns.map((k) => <TableHead key={k} className="whitespace-nowrap">{prettyKey(k)}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {items.map((row, i) => (
                    <TableRow key={i} data-testid="report-rows-row">{columns.map((k) => <TableCell key={k} className="text-xs max-w-[22rem] break-words">{cell(row[k])}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <DialogFooter className="flex items-center justify-between sm:justify-between w-full">
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" disabled={page <= 1 || view.loading} data-testid="report-rows-prev" onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={!body?.has_more || view.loading} data-testid="report-rows-next" onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
          {request?.exportHref && <a href={request.exportHref} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="report-rows-export">Export the whole view (all rows)</Button></a>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
